import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { returnFound } from "find-and";
import Tree, { RenderItemParams, TreeItem } from "@atlaskit/tree";

import { LayerGroupList, LayerListContainer } from "./styled";
import { EmptyLayerList } from "./components/EmptyLayerList/EmptyLayerList";
import { RecommendedLayers } from "./components/RecommendedLayers/RecommendedLayers";
import { DraggableLayerItem } from "./components/DraggableLayerItem";
import { DraggableLayerGroup } from "./components/DraggableLayerGroup";
import { NothingFound } from "./components/LayerListSearch/NothingFound";
import { LayerListSearch } from "./components/LayerListSearch/LayerListSearch";
import { LayersSearchProvider } from "./components/LayerListSearch/context";
import { getTempSourceLayers } from "../../ducks/tempSourceLayers/selectors";
import { useLayerListModule } from "../../hooks/appConfig/useAppModule";
import { useProjectName } from "../../hooks/project/useProjectName";
import { useLayersTree } from "../../hooks/project/useLayersTree/useLayersTree";
import { useLayersTreeSelection } from "./hooks/useLayersTreeSelection";
import { useProjectLayers } from "../../hooks/project/useProjectLayers";
import { useIsShared } from "../../hooks/api/useIsShared";
import { SearchMode } from "./components/LayerListSearch/types";
import { ModuleConfig } from "../../appConfig/types";
import { DraggableLayerGroupProps } from "./types";

const EXPANDING_TIMEOUT = 500;

// eslint-disable-next-line max-lines-per-function
export const LayerListModule: FC<{
  isSelectable?: boolean;
  noSearch?: boolean;
  noMenu?: boolean;
  noControl?: boolean;
  noGroups?: boolean;
  noLegend?: boolean;
  noScroll?: boolean;
  noDrag?: boolean;
  noVisibilityClick?: boolean;
  filterByConfig?: boolean;
  hasGeometry?: boolean;
  layerNames?: string[];
}> = ({
  isSelectable,
  noSearch,
  noMenu,
  noControl,
  noGroups,
  noLegend,
  noScroll,
  noDrag,
  noVisibilityClick,
  filterByConfig,
  hasGeometry,
  layerNames,
}) => {
  const isShared = useIsShared();
  const projectName = useProjectName();
  const layers = useProjectLayers();
  const layerListModule = useLayerListModule();
  const [searchFilter, setSearchFilter] = useState("");
  const [searchMode, setSearchMode] = useState<SearchMode>();
  const [expandingId, setExpandingId] = useState("");
  const { tree, onTreeExpand, onTreeDragEnd } = useLayersTree({
    layerNames,
    searchFilter,
    noGroups,
    filterByConfig,
    hasGeometry,
  });
  const { selected, isSelected, onSelect } = useLayersTreeSelection(tree);
  const tempSourceLayers = useSelector(getTempSourceLayers);
  const expandingRef = useRef(0);

  const noLayersInMap = projectName && layers?.length === 0 && tempSourceLayers.length === 0;
  const { options: { recommendedLayers } = {} } = layerListModule || ({ options: {} } as ModuleConfig);

  const onGroupExpand = useCallback(
    (item: TreeItem) => {
      setExpandingId(item.isExpanded ? "" : (item.id as string));
      onTreeExpand(item);
    },
    [onTreeExpand],
  );

  const isExpanding = useCallback(
    (item: TreeItem) => {
      const expandingGroup = returnFound(layers, { name: expandingId });

      return expandingGroup?.children?.some(({ name }) => name === item.id);
    },
    [expandingId, layers],
  );

  const formattedSelected = useMemo(() => selected.map(({ id }) => id.toString()), [selected]);

  const isEmptyLayers = useMemo(
    () => searchMode === SearchMode.Layers && Object.keys(tree.items).length <= 1,
    [searchMode, tree.items],
  );

  const renderItem = useCallback(
    ({ item, provided, snapshot }: RenderItemParams) => {
      const isSearchMode = searchMode === SearchMode.Selection;
      const props: DraggableLayerGroupProps = {
        layer: item.data,
        provided,
        snapshot,
        noMenu,
        noControl,
        noLegend,
        noDrag,
        noVisibilityClick,
        noIcon: isSearchMode,
        isExpanding: isExpanding(item),
        isSelectable: isSelectable || isSearchMode,
        isSelected: isSelected(item),
        toggleSelected: () => onSelect(item),
      };

      return item.children ? (
        <DraggableLayerGroup {...props} onGroupExpand={() => onGroupExpand(item)} />
      ) : (
        <DraggableLayerItem {...props} />
      );
    },
    [
      isExpanding,
      isSelectable,
      isSelected,
      noControl,
      noDrag,
      noLegend,
      noMenu,
      noVisibilityClick,
      onGroupExpand,
      onSelect,
      searchMode,
    ],
  );

  useEffect(() => {
    if (expandingId) {
      expandingRef.current = window.setTimeout(() => {
        setExpandingId("");
        clearTimeout(expandingRef.current);
      }, EXPANDING_TIMEOUT);
    }
  }, [expandingId]);

  useEffect(
    () => () => {
      clearTimeout(expandingRef.current);
    },
    [],
  );

  return (
    <LayerListContainer>
      {noLayersInMap ? (
        <>
          <EmptyLayerList />
          {recommendedLayers && <RecommendedLayers />}
        </>
      ) : (
        <>
          {!noSearch && (
            <LayersSearchProvider>
              <LayerListSearch
                searchFilter={searchFilter}
                searchMode={searchMode}
                selected={formattedSelected}
                isDisabled={!projectName}
                setSearchFilter={setSearchFilter}
                setSearchMode={setSearchMode}
              />
            </LayersSearchProvider>
          )}
          {isEmptyLayers ? (
            <NothingFound searchMode={SearchMode.Layers} />
          ) : (
            (!searchFilter || [SearchMode.Layers, SearchMode.Selection].includes(searchMode)) && (
              <LayerGroupList $noScroll={noScroll}>
                <Tree
                  tree={tree}
                  offsetPerLevel={10}
                  isDragEnabled={!isShared && !noControl && !noDrag}
                  isNestingEnabled={!isShared}
                  onDragEnd={onTreeDragEnd}
                  renderItem={renderItem}
                />
              </LayerGroupList>
            )
          )}
        </>
      )}
    </LayerListContainer>
  );
};
